import { useEffect, useState, useMemo } from "react";
import Navbar from "@/components/app/Navbar";
import Footer from "@/components/app/Footer";
import ListingCard from "@/components/app/ListingCard";
import { api } from "@/lib/api";
import { Search } from "lucide-react";

export default function Listings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [addon, setAddon] = useState("");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (city) params.city = city;
    if (maxPrice) params.max_price = maxPrice;
    if (addon) params.addon = addon;
    try {
      const { data } = await api.get("/listings", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSubmit = (e) => { e.preventDefault(); load(); };

  const addonOptions = useMemo(
    () => ["", "cook", "cleaning", "wifi_boost", "airport_pickup"],
    [],
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="overline">Browse</div>
            <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl tracking-tight">All long-stay homes</h1>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="surface-card p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-3 mb-10"
          data-testid="filter-form"
        >
          <div className="md:col-span-4">
            <label className="overline">City</label>
            <input
              className="input-earthy mt-2"
              placeholder="e.g. Kharghar"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="filter-city"
            />
          </div>
          <div className="md:col-span-3">
            <label className="overline">Max price / night</label>
            <input
              type="number"
              min={0}
              className="input-earthy mt-2"
              placeholder="₹"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              data-testid="filter-max-price"
            />
          </div>
          <div className="md:col-span-3">
            <label className="overline">Add-on</label>
            <select
              className="input-earthy mt-2"
              value={addon}
              onChange={(e) => setAddon(e.target.value)}
              data-testid="filter-addon"
            >
              {addonOptions.map((a) => (
                <option key={a} value={a}>{a ? a.replace("_", " ") : "Any"}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 flex items-end">
            <button type="submit" className="btn-pill btn-primary w-full" data-testid="filter-apply">
              <Search className="w-4 h-4" /> Search
            </button>
          </div>
        </form>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="surface-card h-[500px] animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="surface-card p-12 text-center" data-testid="no-results">
            <p className="font-serif-display text-2xl mb-2">No stays match your filters.</p>
            <p className="text-sm" style={{ color: "var(--text-2)" }}>Try clearing some filters or check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="listings-grid">
            {items.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
